<script setup lang="ts">
/**
 * COMMON actions: the right-aligned, icon-only row at the top of a record
 * sidebar — the record-INDEPENDENT tools that do something *with* what is open
 * rather than *to* it: print, PDF, mail, export, history.
 *
 * Icon only: an item's `label` becomes the button's `title` AND
 * `aria-label`. An item without an icon gets an info glyph rather than a blank
 * square. The row is a `toolbar` with a roving tab stop: Tab enters it once,
 * the arrow keys (and Home/End) move between the enabled buttons.
 *
 * `groupSeparators` draws a vertical hairline wherever the items' `group`
 * changes (undo/redo | copy/paste | delete), so one toolbar carries several
 * blocks without its own dividers. The separators are not tab stops.
 *
 * The kit version of the ERP's `CommonActions`, with the same `items` shape
 * (`NxActionElement`) and `label`.
 */
import { InfoIcon } from '@blpde/nx-icons';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { NxButton } from '../Button';
import type { NxActionElement } from './types';

const props = defineProps<{
  items: readonly NxActionElement[];
  /** Names the toolbar for assistive technology. */
  label?: string;
  /** A separator between two neighbouring items whose `group` differs. Off by default. */
  groupSeparators?: boolean;
}>();

/** Item indexes that start a new group, i.e. get a separator before them. */
const groupStarts = computed(() => {
  const starts = new Set<number>();
  if (!props.groupSeparators) return starts;
  props.items.forEach((item, index) => {
    if (index > 0 && item.group !== props.items[index - 1]!.group) starts.add(index);
  });
  return starts;
});

const rowRef = useTemplateRef<HTMLElement>('row');

/** Index of the button that holds the tab stop. */
const current = ref(0);

const enabled = computed(() =>
  props.items.map((item, index) => (item.disabled ? -1 : index)).filter((i) => i >= 0)
);

// Keep the tab stop on an enabled button when the list or the flags change.
watch(
  enabled,
  (list) => {
    if (!list.includes(current.value)) current.value = list[0] ?? 0;
  },
  { immediate: true }
);

function focusIndex(index: number) {
  current.value = index;
  const buttons = rowRef.value?.querySelectorAll<HTMLButtonElement>(':scope > button');
  buttons?.[index]?.focus();
}

function onKeydown(event: KeyboardEvent) {
  const list = enabled.value;
  if (!list.length) return;
  const pos = Math.max(0, list.indexOf(current.value));
  let next: number;
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      next = list[(pos + 1) % list.length]!;
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      next = list[(pos - 1 + list.length) % list.length]!;
      break;
    case 'Home':
      next = list[0]!;
      break;
    case 'End':
      next = list[list.length - 1]!;
      break;
    default:
      return;
  }
  event.preventDefault();
  focusIndex(next);
}
</script>

<template>
  <div
    ref="row"
    class="nx-action-toolbar"
    role="toolbar"
    :aria-label="props.label"
    @keydown="onKeydown"
  >
    <template v-for="(item, idx) in props.items" :key="item.id ?? idx">
      <span
        v-if="groupStarts.has(idx)"
        class="nx-action-toolbar__separator"
        role="separator"
        aria-orientation="vertical"
      />
      <NxButton
        class="nx-action-toolbar__btn"
        width="icon-only"
        height="s"
        :variant="item.tone === 'danger' ? 'outline-danger' : 'outline'"
        :title="item.label"
        :aria-label="item.label"
        :disabled="item.disabled"
        :data-test="item.dataTest"
        :tabindex="idx === current ? 0 : -1"
        @focus="current = idx"
        @click="item.onClick()"
      >
        <component
          :is="item.icon.component"
          v-if="item.icon"
          :filled="item.icon.filled"
          :color="item.icon.color"
          class="nxt-icon-lg"
          aria-hidden="true"
        />
        <InfoIcon
          v-else
          :filled="true"
          class="nxt-icon-lg"
          color="var(--nxt-fg-muted)"
          aria-hidden="true"
        />
      </NxButton>
    </template>
  </div>
</template>

<style scoped>
.nx-action-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--nxt-space-xs);
  width: 100%;
  padding-inline: var(--nxt-space-xs);
}
.nx-action-toolbar__separator {
  align-self: stretch;
  width: var(--nxt-hairline);
  margin-inline: var(--nxt-space-2xs);
  background: var(--nxt-border);
}
</style>
